import { useState, useEffect } from 'react';
import './ReportsPage.css';
import '../../styles/PageCommon.css';
import SegmentedControl from '../../components/common/SegmentedControl/SegmentedControl';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calendar-icon.svg';
import { ReactComponent as ArrowDownIcon } from '../../assets/icons/arrow-down-icon.svg';
import DemandPlanningReport from '../../components/DemandPlanningReport/DemandPlanningReport';
import CalendarModal from '../../components/common/CalendarModal/CalendarModal';
import { ReactComponent as ChevronLeft } from '../../assets/icons/chevron-left-icon.svg';
import { ReactComponent as EditIcon } from '../../assets/icons/edit-icon.svg';
import { ReactComponent as DownloadIcon } from '../../assets/icons/download-icon.svg';
import { ReactComponent as LoadingIcon } from '../../assets/icons/upload-loading-icon.svg';
import { useNavigate } from 'react-router-dom';
import { useParams } from "react-router-dom";
import { reportsService } from '../../services/reportsService';
import { formatToReadableDate, mergePlotData } from '../../utils/utils';
import LoadingAnimation from '../../components/common/LoadingAnimation/LoadingAnimation';
import { useFilterParams } from '../../hooks/useFilterParams';
import { AccordionCheckboxSelector } from '../../components/common/AccordionCheckboxSelector/AccordionCheckboxSelector';
import MultiSelectDropdown from '../../components/common/MultiSelectDropdown';
import { useAuth } from '../../contexts/AuthContext';
import { NotificationContainer } from '../../components/common/NotificationContainer';
import NotificationModal from '../../components/common/NotificationModal';
import { parseApiError } from '../../utils/parseErrorMessage';
import PromoCalendarModal from '../../components/PromoCalendar/PromoCalendarModal';

const DEFAULT_FILTERS = {
  date_from: null,
  date_to: null,
  view_type: "DSP",
  sku_code: [],
  brand: [],
  category: [],
  sub_category: [],
  subline: [],
  branch_name: [],
  hub_name: [],
  sku_status: [],
};

const clampDatesToRange = (filters, range) => {
  if (!range?.minDate || !range?.maxDate) return null;
  const min = new Date(range.minDate);
  const max = new Date(range.maxDate);
  const updates = {};
  if (filters.date_from) {
    const from = new Date(filters.date_from);
    if (from < min) updates.date_from = range.minDate;
    else if (from > max) updates.date_from = range.maxDate;
  }
  if (filters.date_to) {
    const to = new Date(filters.date_to);
    if (to > max) updates.date_to = range.maxDate;
    else if (to < min) updates.date_to = range.minDate;
  }
  return Object.keys(updates).length ? updates : null;
};

export default function ReportsPage() {
  const { fetchUserInfo } = useAuth();

  const [isLoadingData, setIsLoadingData] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const navigate = useNavigate();

  const { reportId } = useParams();

  const [isProductsFilterOpen, setIsProductsFilterOpen] = useState(false);
  const [isCalendarFilterOpen, setIsCalendarFilterOpen] = useState(false);
  const [isPromoOpen, setIsPromoOpen] = useState(false);
  const [reportData, setReportData] = useState({});
  const [plotData, setPlotData] = useState([]);
  const [availableDateRange, setAvailableDateRange] = useState(null);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(reportData?.report?.report_name || "");
  const [dataError, setDataError] = useState("")

  // --- Filters State and Logic --- 
  const { filters, updateFilters } = useFilterParams(DEFAULT_FILTERS);
  const productFiltersArray = Object.values({
    sku_code:     filters.sku_code     || [],
    brand:        filters.brand        || [],
    category:     filters.category     || [],
    sub_category: filters.sub_category || [],
    subline:      filters.subline      || [],
  }).flat();

  const fetchReportData = () => {
    setIsLoadingData(true);
    reportsService.getReportById(reportId, filters)
      .then(reportData => {
        if (!reportData) return;
        setReportData(reportData);
        setPlotData(mergePlotData(reportData.historical_table, reportData.forecast_table));
        if (reportData.min_date && reportData.max_date) {
          setAvailableDateRange(prev =>
            prev?.minDate === reportData.min_date && prev?.maxDate === reportData.max_date
              ? prev
              : { minDate: reportData.min_date, maxDate: reportData.max_date }
          );
        }
      })
      .catch(error => setDataError(parseApiError(error) || ""))
      .finally(() => setIsLoadingData(false));
  };

  useEffect(() => {
    const clamped = clampDatesToRange(filters, availableDateRange);
    if (clamped) {
      updateFilters(clamped, false);
      return;
    }
    fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, filters, availableDateRange]);

  useEffect(() => {
    if (reportData?.report?.report_name) {
      setTitleValue(reportData.report.report_name);
    }
  }, [reportData]);

  // eslint-disable-next-line no-unused-vars
  const handleSave = () => {

  }

  const handleUpdateReportName = () => {
    setIsLoadingData(true);
    const updated = {
        report_name: titleValue,
        forecast_adjustments: []
    }

    console.log("updateReport data: ", updated);
    reportsService.updateReport(reportData.report.report_id, filters, updated)
      .then(response => {
          console.log("updateReport data: ", response);
          if (response) {
              fetchUserInfo();
              fetchReportData();
          }
      })
      .catch(error => {
        console.error("Error fetching reports: ", error);
        
        const errorMessage = parseApiError(error)
        setDataError(errorMessage ? errorMessage : "");
      })
      .finally(() => {
          setIsLoadingData(false);
      });
  }

  // eslint-disable-next-line no-unused-vars
  const handleDeleteReport = () => {
    setIsLoadingData(true);
    reportsService.deleteReportById(reportData.report.report_id)
      .then(() => {
        fetchUserInfo();
        navigate("/reports/list");
      })
      .catch(error => {
        console.error("Error deleting report: ", error);
        
        const errorMessage = parseApiError(error)
        setDataError(errorMessage ? errorMessage : "");
      })
      .finally(() => {
        setIsLoadingData(false);
      });
  }

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const response = await reportsService.downloadExcel(reportId, filters);

      const blob = new Blob([response.data], { type: response.headers["content-type"] });
      const url = window.URL.createObjectURL(blob);

      const contentDisposition = response.headers["content-disposition"];
      let filename = "report.xlsx";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match?.[1]) filename = match[1];
      }

      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setDataError(parseApiError(error) || "");
    }
    setDownloading(false);
  };

  const handleTitleBlur = () => {
    setIsEditingTitle(false);
    if (titleValue?.trim() && titleValue !== reportData?.report?.report_name) {
      handleUpdateReportName();
    }
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    }
    if (e.key === "Escape") {
      setTitleValue(reportData?.report?.report_name);
      setIsEditingTitle(false);
    }
  };

  return (
    <>
      {isLoadingData && <LoadingAnimation />}
        <NotificationContainer>
          {
            dataError && <NotificationModal closeModal={() => setDataError(null)} type="error" subTitle={dataError} title="Ошибка" duration={3000}/>
          }
        </NotificationContainer>
      <div className="page-content">
        <div className='page-path'>
            <span className='page-path-previous' style={{cursor: "pointer"}} onClick={() => navigate("/reports/list")}>
                ДП Отчет
            </span>
            <ChevronLeft color='#4A4A4A' />
            <span className='page-path-current'>
                {reportData?.report?.report_name}
            </span>
        </div>

        <div className="reports-header-container">
          {
            isEditingTitle ? (
              <div className='form-input'
                style={{ border: "none", padding: "4px 8px", backgroundColor: "rgb(247 248 249)" }}>
                <input
                  className="reports-title reports-title--editing"
                  style={{ 
                    border: "0px", flex: 1, outline: "none", 
                    fontSize: 28, textDecoration: "underline",
                    backgroundColor: "rgb(247 248 250)" 
                  }}
                  value={titleValue}
                  autoFocus
                  onChange={(e) => setTitleValue(e.target.value)}
                  onBlur={handleTitleBlur}
                  onKeyDown={handleTitleKeyDown}
                />
              </div>

              ) 
              : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <h1
                    className="reports-title"
                    onDoubleClick={() => setIsEditingTitle(true)}
                  >
                    {titleValue}
                  </h1>
                  <EditIcon
                    width={20}
                    color="#7CC805"
                    className="reports-title-edit-icon"
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                    onClick={() => setIsEditingTitle(true)}
                  />
                </div>
              )
          }
          <div className='report-header-buttons'>
            <button type="button" className="secondary-button" onClick={() => setIsPromoOpen(true)}>
              Промо-календарь
            </button>
            <button type="button" className="primary-button" onClick={handleDownloadExcel} disabled={downloading}>
              <DownloadIcon size={24} color="#FFFFFF"/>
              {downloading
                ? <LoadingIcon size={20} color="#ffffff" className="loading-icon spinning"/>
                : "Загрузить Excel"}
            </button>
            {
              isPromoOpen &&
              <PromoCalendarModal
                filters={filters}
                productFiltersArray={productFiltersArray}
                updateFilters={updateFilters}
                isOpen={isPromoOpen}
                closeModal={() => setIsPromoOpen(false)}
                />
            }
          </div>
        </div>
        
        <div className="dp-chart-filters-container">

            <button
              onClick={() => setIsProductsFilterOpen(true)}
              className="filter-select"
            >
              <span className="filter-select-label">Продукты:</span>
              <span className="filter-select-option">
                {
                  productFiltersArray.join(", ")
                }
              </span>

              {
                productFiltersArray.length > 0
                && <span style={{ background: "#7CC805", color: "#fff", borderRadius: 20, fontSize: 11, fontWeight: 700, padding: "1px 6px", flexShrink: 0 }}>
                  {
                    productFiltersArray.length
                  }
                  </span>
              }
              <ArrowDownIcon size={24} color='#7CC805'/>
            </button>
            {
              isProductsFilterOpen && 
                <AccordionCheckboxSelector 
                  selectedOptions={(({ page, page_size, view_type, branch_name, date_to, date_from, ...rest }) => rest)(filters)}
                  filterOptions={productFiltersArray}
                  closeModal={() => setIsProductsFilterOpen(false)} 
                  onApply={(selectedFilter) => {updateFilters(selectedFilter)}}
                />
            }
            
            <MultiSelectDropdown
              label="Статус СКЮ"
              placeholder="Все"
              selectedOptions={filters.sku_status}
              options={reportData?.report?.sku_status_filter}
              onChange={(values) => {updateFilters({sku_status: values})}}
            />
            
            <MultiSelectDropdown
              label="Склад"
              placeholder="Все"
              selectedOptions={filters.branch_name}
              options={reportData?.report?.branch_filter}
              onChange={(values) => {updateFilters({branch_name: values})}}
            />
            
            <MultiSelectDropdown
              label="РЦ"
              placeholder="Все"
              selectedOptions={filters.hub_name}
              options={reportData?.report?.hub_filter}
              onChange={(values) => {updateFilters({hub_name: values})}}
            />

            <SegmentedControl
              value={filters?.view_type?.toLowerCase()}
              onChange={(value) => updateFilters({ view_type: value })}
              options={[
                {
                  label: "Деньги",
                  options: [
                    { label: "Прайс-лист", value: "dsp" },
                    { label: "Инвойс цена", value: "invoice price" },
                  ],
                },
                { label: "Короб", value: "cases" },
                { label: "Вес", value: "net weight" },
              ]}
            />

            <button
              onClick={() => {console.log("isCalendarFilterOpen ", isCalendarFilterOpen);setIsCalendarFilterOpen(true)}}
              className="filter-select"
            >              
              <CalendarIcon size={16} color='#7CC805'/>
              <span className="filter-select-label">{formatToReadableDate(filters.date_from)} - {formatToReadableDate(filters.date_to)}</span>
              <ArrowDownIcon size={24} color='#7CC805'/>
            </button>
            {
              isCalendarFilterOpen && 
                <CalendarModal title={"Выберите период"} 
                  isOpen={isCalendarFilterOpen} 
                  closeModal={()=>{setIsCalendarFilterOpen(false)}} 
                  dateRange={{ startDate: filters.date_from, endDate: filters.date_to }} 
                  applyDateRangeFilter={(dateRange) => { 
                    updateFilters({ date_from: dateRange.startDate, date_to: dateRange.endDate }); 
                  }}
                  minDate={availableDateRange?.minDate}
                  maxDate={availableDateRange?.maxDate}
                />
            }
        </div>

        <DemandPlanningReport data={plotData} planningMonth={reportData?.report?.planning_month}/>
      </div>
    </>
  );
}

